/* =========================================================================
   THE POOR BOX — almsgiving (PLAN-gameplay-v2 §1, db/047).

   Give game-dollars to the monastery's works of mercy. Eric's rule was to be
   GENEROUS with the feel-good payoff, and the plan's reverence policy says what
   that payoff may be: warmth and identity, never power.

   So this surface pays back in exactly four currencies, all of them free:
     • a warm Brother Theo blessing, written per cause (db/047 AlmsCauses),
     • a coin-into-the-box animation with a slow glow (honoured reduced motion),
     • a LIFETIME GIVING total that never resets and is never spent,
     • milestone titles — First Alms → Servant of the Least — thanked for once,
       when crossed, by Theo.

   And it pays back in nothing else. There is no XP, no bead, no unlock, no
   streak credit, no serving advantage, no leaderboard, no comparison to other
   players anywhere in this file. If a future change wants to add one, the
   answer from the plan is no: charity is honored here, never transactional.
   (CatholicsApp.Core.Almsgiving carries the same rule with a test guarding it.)

   The causes are the monastery's OWN in-fiction works — no real-world charity
   and no real money is involved at any point.

   This is a surface the player OPENS DELIBERATELY from the monastery, so it
   does NOT go through the Moment Director: it is not an interstitial and it
   never interrupts play.

   Money is server-authoritative: POST /api/alms/donate debits inside
   app.usp_Ledger_Post (idempotent, UPDLOCK, rejects an overdraft). Nothing here
   mutates a balance locally — it only reflects what the server returned.

   CSS prefix: `alms-` (grepped clean before use).
   ========================================================================= */
import { esc, fmtCents } from './engine';
import { icon } from './ui/icons';
import { track } from './telemetry';
import { prefersReducedMotion } from './ui/momentOverlay';
import { isAuthenticated, getAlms, donateAlms, type AlmsCause, type AlmsMilestone, type AlmsResponse } from './api';

const OVERLAY_ID = 'poorBoxOv';
const EXIT_MS = 260;

/* ------------------------------- pure logic ------------------------------- */

/** The suggested-amount ladder, in cents. Deliberately wide: the plan's sink is
    a six-figure wallet, and a $100 chip next to a $113,514 balance reads as a
    joke rather than an invitation. */
export const PRESET_LADDER_CENTS = [
  10_000, 50_000, 100_000, 500_000, 1_000_000, 2_500_000, 5_000_000, 10_000_000,
];

/**
 * PURE. Up to four suggested amounts for this wallet: the largest rungs the
 * player can actually afford, ascending. A wallet too small for any rung gets
 * its own whole-dollar balance as the single suggestion (so someone with $63
 * still sees a real option), and an empty wallet gets nothing to press.
 */
export function almsPresets(walletCents: number | null, minCents = 100, maxCents = 100_000_000): number[] {
  const wallet = walletCents == null || walletCents < 0 ? 0 : walletCents;
  const ceiling = Math.min(wallet, maxCents);
  if (ceiling < minCents) return [];
  const affordable = PRESET_LADDER_CENTS.filter((c) => c <= ceiling && c >= minCents);
  if (affordable.length === 0) {
    const wholeDollars = Math.floor(ceiling / 100) * 100;
    return wholeDollars >= minCents ? [wholeDollars] : [];
  }
  return affordable.slice(-4);
}

/**
 * PURE. Validate a typed amount against the wallet and the server's bounds.
 * Returns the amount to send, or an honest reason it can't be sent. The copy
 * here is deliberately gentle — nobody should be told off for trying to give.
 */
export function validateGift(
  amountCents: number,
  walletCents: number | null,
  minCents = 100,
  maxCents = 100_000_000,
): { ok: true; cents: number } | { ok: false; reason: string } {
  if (!Number.isFinite(amountCents) || amountCents <= 0) {
    return { ok: false, reason: 'Choose an amount to give.' };
  }
  const cents = Math.floor(amountCents);
  if (cents < minCents) return { ok: false, reason: `The box takes gifts of ${fmtCents(minCents)} and up.` };
  if (cents > maxCents) return { ok: false, reason: `${fmtCents(maxCents)} is the most the box can take at once.` };
  if (walletCents != null && cents > walletCents) {
    return { ok: false, reason: `That is more than your wallet holds. You have ${fmtCents(walletCents)}.` };
  }
  return { ok: true, cents };
}

/** PURE. 0–100 progress from the previous rung to the next. 100 once the ladder
    is finished (nextMilestone null). */
export function milestoneProgressPct(
  lifetimeCents: number,
  current: AlmsMilestone | null,
  next: AlmsMilestone | null,
): number {
  if (!next) return 100;
  const floor = current ? current.atCents : 0;
  const span = next.atCents - floor;
  if (span <= 0) return 0;
  const pct = Math.round(((lifetimeCents - floor) / span) * 100);
  return Math.min(100, Math.max(0, pct));
}

/** PURE. The lifetime-giving line. Never comparative, never a rank — just what
    this person has quietly done. */
export function lifetimeLine(lifetimeCents: number, donationCount: number): string {
  if (donationCount <= 0) return 'The box is waiting. Nothing given yet, and no hurry.';
  const gifts = donationCount === 1 ? '1 gift' : `${donationCount.toLocaleString('en-US')} gifts`;
  return `${fmtCents(lifetimeCents)} given, across ${gifts}.`;
}

/* ------------------------------- overlay state ------------------------------- */

let panel: AlmsResponse | null = null;
let chosenCause: string | null = null;
let chosenCents = 0;
let sending = false;

/** Drop cached giving state on sign-out — it is per-account. */
export function resetAlms(): void {
  panel = null;
  chosenCause = null;
  chosenCents = 0;
}

/* --------------------------------- rendering --------------------------------- */

function causeTile(c: AlmsCause): string {
  const sel = chosenCause === c.key ? ' is-chosen' : '';
  return (
    `<button class="alms-cause${sel}" type="button" data-cause="${esc(c.key)}"` +
    ` aria-pressed="${chosenCause === c.key ? 'true' : 'false'}">` +
    `<span class="alms-cause-ic" aria-hidden="true">${c.icon ? esc(c.icon) : icon('dove', 22)}</span>` +
    `<span class="alms-cause-main"><span class="alms-cause-name">${esc(c.name)}</span>` +
    (c.descr ? `<span class="alms-cause-descr">${esc(c.descr)}</span>` : '') +
    `</span></button>`
  );
}

function lifetimePanel(): string {
  if (!panel) return '';
  const pct = milestoneProgressPct(panel.lifetimeCents, panel.milestone, panel.nextMilestone);
  const title = panel.milestone
    ? `<div class="alms-title-badge">${esc(panel.milestone.title)}</div>`
    : '';
  const toward = panel.nextMilestone
    ? `<div class="alms-toward">${esc(fmtCents(panel.nextMilestone.atCents - panel.lifetimeCents))} more to ${esc(panel.nextMilestone.title)}</div>`
    : `<div class="alms-toward">Every rung of the ladder, walked.</div>`;
  return (
    `<div class="alms-lifetime">` +
    `<div class="alms-lifetime-lbl">Lifetime giving</div>` +
    `<div class="alms-lifetime-val" id="almsLifetimeVal">${esc(fmtCents(panel.lifetimeCents))}</div>` +
    title +
    `<div class="alms-lifetime-sub">${esc(lifetimeLine(panel.lifetimeCents, panel.donationCount))}</div>` +
    `<div class="alms-mile-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"` +
    ` aria-label="Progress toward the next giving milestone"><span class="alms-mile-fill" style="width:${pct}%"></span></div>` +
    toward +
    `</div>`
  );
}

function amountRow(): string {
  if (!panel) return '';
  const presets = almsPresets(panel.walletBalanceCents, panel.minDonationCents, panel.maxDonationCents);
  const chips = presets
    .map(
      (c) =>
        `<button class="alms-chip${chosenCents === c ? ' is-chosen' : ''}" type="button" data-cents="${c}"` +
        ` aria-pressed="${chosenCents === c ? 'true' : 'false'}">${esc(fmtCents(c))}</button>`,
    )
    .join('');
  return (
    `<div class="alms-amount">` +
    `<div class="alms-section-lbl">How much?</div>` +
    `<div class="alms-chips">${chips || '<span class="alms-empty">Your wallet is empty just now.</span>'}</div>` +
    `<label class="alms-other"><span class="alms-other-lbl">Or another amount</span>` +
    `<input id="almsOther" class="alms-other-input" type="number" inputmode="numeric" min="1" step="1"` +
    ` placeholder="$" aria-label="Another amount, in dollars" /></label>` +
    `<div class="alms-wallet">${icon('coin', 16)} ${esc(fmtCents(panel.walletBalanceCents))} in your wallet</div>` +
    `</div>`
  );
}

function bodyHtml(): string {
  if (!panel) return `<div class="alms-loading">Opening the box…</div>`;
  return (
    lifetimePanel() +
    `<div class="alms-section-lbl">Where should it go?</div>` +
    `<div class="alms-causes">${panel.causes.map(causeTile).join('')}</div>` +
    amountRow() +
    `<div class="alms-err" id="almsErr" role="alert" aria-live="polite"></div>` +
    `<button class="btn alms-give" id="almsGive" type="button"${chosenCause && chosenCents > 0 ? '' : ' disabled'}>` +
    `${chosenCents > 0 ? `Give ${esc(fmtCents(chosenCents))}` : 'Give'}</button>` +
    `<div class="alms-fineprint">The monastery's own works, inside the game. No real money, and nothing given here ` +
    `changes how you play. It is simply yours to have done.</div>`
  );
}

function shellHtml(): string {
  return (
    `<div class="alms-card" id="almsCard">` +
    `<button class="alms-close" id="almsClose" type="button" aria-label="Close the Poor Box">×</button>` +
    `<div class="alms-box" id="almsBox" aria-hidden="true"><span class="alms-box-ic">${icon('coin', 36)}</span></div>` +
    `<div class="alms-kicker">Almsgiving</div>` +
    `<h2 class="alms-h">The Poor Box</h2>` +
    `<div class="alms-body" id="almsBody">${bodyHtml()}</div>` +
    `</div>`
  );
}

/** The thank-you state: coins, glow, Theo, and (when earned) a new title. */
function thanksHtml(
  causeName: string,
  amountCents: number,
  blessing: string | null,
  lifetimeCents: number,
  donationCount: number,
  reached: AlmsMilestone | null,
): string {
  const coins = prefersReducedMotion()
    ? ''
    : `<div class="alms-coins" aria-hidden="true">` +
      [0, 1, 2, 3, 4].map((i) => `<span class="alms-coin alms-coin-${i}">${icon('coin', 18)}</span>`).join('') +
      `</div>`;
  const milestone = reached
    ? `<div class="alms-milestone">` +
      `<div class="alms-milestone-kicker">A new name in the book by the door</div>` +
      `<div class="alms-milestone-title">${esc(reached.title)}</div>` +
      `<div class="alms-milestone-note">${esc(reached.note)}</div>` +
      `</div>`
    : '';
  return (
    coins +
    `<div class="alms-thanks">` +
    `<div class="alms-thanks-amt">${esc(fmtCents(amountCents))}</div>` +
    `<div class="alms-thanks-cause">to ${esc(causeName)}</div>` +
    (blessing
      ? `<div class="alms-blessing"><span class="alms-blessing-who">Brother Theo</span>` +
        `<span class="alms-blessing-line">“${esc(blessing)}”</span></div>`
      : '') +
    milestone +
    `<div class="alms-thanks-life">Lifetime giving · ${esc(fmtCents(lifetimeCents))}` +
    ` <span class="alms-thanks-count">(${donationCount === 1 ? '1 gift' : `${donationCount} gifts`})</span></div>` +
    `<button class="btn alms-again" id="almsAgain" type="button">Give again</button>` +
    `<button class="btn secondary alms-done" id="almsDone" type="button">Close</button>` +
    `</div>`
  );
}

/* ---------------------------------- wiring ---------------------------------- */

function setErr(msg: string): void {
  const e = document.getElementById('almsErr');
  if (e) e.textContent = msg;
}

function repaintBody(): void {
  const body = document.getElementById('almsBody');
  if (!body) return;
  body.innerHTML = bodyHtml();
  wireBody();
}

function wireBody(): void {
  const body = document.getElementById('almsBody');
  if (!body) return;

  body.querySelectorAll<HTMLButtonElement>('.alms-cause').forEach((b) => {
    b.onclick = () => {
      chosenCause = b.dataset.cause || null;
      repaintBody();
    };
  });

  body.querySelectorAll<HTMLButtonElement>('.alms-chip').forEach((b) => {
    b.onclick = () => {
      chosenCents = Number(b.dataset.cents || 0);
      repaintBody();
    };
  });

  const other = document.getElementById('almsOther') as HTMLInputElement | null;
  if (other) {
    other.oninput = () => {
      const dollars = Number(other.value);
      chosenCents = Number.isFinite(dollars) && dollars > 0 ? Math.floor(dollars) * 100 : 0;
      const give = document.getElementById('almsGive') as HTMLButtonElement | null;
      if (give) {
        give.disabled = !(chosenCause && chosenCents > 0);
        give.textContent = chosenCents > 0 ? `Give ${fmtCents(chosenCents)}` : 'Give';
      }
      // A typed amount clears any chip highlight without a full repaint (which
      // would steal focus from the field mid-keystroke).
      body.querySelectorAll<HTMLButtonElement>('.alms-chip').forEach((c) => {
        c.classList.toggle('is-chosen', Number(c.dataset.cents || 0) === chosenCents);
        c.setAttribute('aria-pressed', Number(c.dataset.cents || 0) === chosenCents ? 'true' : 'false');
      });
    };
  }

  const give = document.getElementById('almsGive') as HTMLButtonElement | null;
  if (give) give.onclick = () => void submitGift();
}

async function submitGift(): Promise<void> {
  if (sending || !panel || !chosenCause) return;
  const v = validateGift(chosenCents, panel.walletBalanceCents, panel.minDonationCents, panel.maxDonationCents);
  if (!v.ok) {
    setErr(v.reason);
    return;
  }
  sending = true;
  setErr('');
  const give = document.getElementById('almsGive') as HTMLButtonElement | null;
  if (give) {
    give.disabled = true;
    give.textContent = 'Giving…';
  }

  try {
    const r = await donateAlms(chosenCause, v.cents);
    if (!r.ok) {
      setErr(r.offline ? 'Offline. The box will still be here when you are back.' : r.error || 'The gift did not go through.');
      if (give) {
        give.disabled = false;
        give.textContent = `Give ${fmtCents(v.cents)}`;
      }
      return;
    }

    // Reflect the server's numbers — never our own arithmetic.
    panel.walletBalanceCents = r.data.walletBalanceCents;
    panel.lifetimeCents = r.data.lifetimeCents;
    panel.donationCount = r.data.donationCount;
    panel.milestone = r.data.milestone;
    panel.nextMilestone = r.data.nextMilestone;

    if (r.data.newlyRecorded) {
      track('AlmsGiven', { cause: r.data.causeKey, cents: r.data.amountCents });
      if (r.data.milestoneReached) track('AlmsMilestone', { key: r.data.milestoneReached.key });
    }

    const body = document.getElementById('almsBody');
    if (body) {
      body.innerHTML = thanksHtml(
        r.data.causeName,
        r.data.amountCents,
        r.data.blessing,
        r.data.lifetimeCents,
        r.data.donationCount,
        r.data.milestoneReached,
      );
      const box = document.getElementById('almsBox');
      if (box && !prefersReducedMotion()) {
        box.classList.remove('alms-box-glow');
        void box.offsetWidth;
        box.classList.add('alms-box-glow');
      }
      const again = document.getElementById('almsAgain');
      if (again) {
        again.addEventListener('click', () => {
          chosenCents = 0;
          repaintBody();
        });
      }
      const done = document.getElementById('almsDone');
      if (done) done.addEventListener('click', () => closeCurrent());
    }
  } finally {
    sending = false;
  }
}

/* ---------------------------------- open/close ---------------------------------- */

let closeCurrent: () => void = () => {};

/**
 * Open the Poor Box. Resolves when it is dismissed. Safe with no DOM (tests).
 * Guests are not shown a fake balance — the caller (monastery.ts) nudges them
 * to sign in instead, and this bails out politely if called anyway.
 */
export async function openPoorBox(): Promise<void> {
  if (typeof document === 'undefined') return;
  if (!isAuthenticated()) return;

  track('AreaOpened', { area: 'poorbox' });

  const prev = document.getElementById(OVERLAY_ID);
  if (prev && prev.parentNode) prev.parentNode.removeChild(prev);

  const root = document.createElement('div');
  root.id = OVERLAY_ID;
  root.className = 'alms-ov';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'The Poor Box, almsgiving');
  root.innerHTML = shellHtml();
  ((document.querySelector('.screen') as HTMLElement | null) || document.body).appendChild(root);

  let settled = false;
  let resolveDone: () => void = () => {};
  const done = new Promise<void>((res) => {
    resolveDone = res;
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      finish();
    }
  }

  function finish(): void {
    if (settled) return;
    settled = true;
    document.removeEventListener('keydown', onKey, true);
    chosenCause = null;
    chosenCents = 0;
    const remove = (): void => {
      if (root.parentNode) root.parentNode.removeChild(root);
    };
    if (prefersReducedMotion()) remove();
    else {
      root.classList.add('alms-out');
      setTimeout(remove, EXIT_MS);
    }
    resolveDone();
  }
  closeCurrent = finish;

  document.addEventListener('keydown', onKey, true);
  const x = document.getElementById('almsClose');
  if (x) x.addEventListener('click', finish);
  // Tapping the dim (but never the card) closes — same rule as the other overlays.
  root.addEventListener('click', (e) => {
    if (e.target === root) finish();
  });

  const r = await getAlms();
  if (r.ok) {
    panel = r.data;
    if (panel.causes.length > 0) chosenCause = panel.causes[0].key;
  }
  if (!settled) {
    const body = document.getElementById('almsBody');
    if (body) {
      body.innerHTML = r.ok
        ? bodyHtml()
        : `<div class="alms-loading">${esc(r.offline ? 'Offline. The box will be here when you are back.' : 'Could not open the box just now.')}</div>`;
      if (r.ok) wireBody();
    }
  }

  await done;
}
